import { apiFetch, socket } from './index';

export async function getConversations(limit = 20, skip = 0) {
  try {
    const response = await apiFetch({
      method: 'GET',
      endpoint: 'conversations',
      params: { limit, skip }
    });
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch conversations:', error);
    throw new Error('Unable to load conversations. Please try again.');
  }
}

export async function createConversation(participantIds, isGroup = false, groupName = null) {
  if (!participantIds || !Array.isArray(participantIds) || participantIds.length === 0) {
    throw new Error('At least one participant is required');
  }
  try {
    const response = await apiFetch({
      method: 'POST',
      endpoint: 'conversations',
      data: { participantIds, isGroup, groupName }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create conversation:', error);
    throw new Error('Unable to create conversation. Please try again.');
  }
}

export async function getMessages(conversationId, limit = 50, skip = 0, abortSignal = null) {
  const response = await apiFetch({
    method: 'GET',
    endpoint: `conversations/${conversationId}/messages`,
    params: { limit, skip },
    abortSignal
  });
  return response.data;
}

export async function sendMessage(conversationId, content, studyTags = [], attachments = [], transcript = null) {
  if (!conversationId) {
    throw new Error('Conversation ID is required');
  }
  if (!content?.trim() && (!attachments || attachments.length === 0) && !transcript) {
    throw new Error('Message content, attachments, or transcript are required');
  }
  try {
    const payload = {
      content: content || '',
      studyTags: Array.isArray(studyTags) ? studyTags : [],
      attachments: Array.isArray(attachments) ? attachments.map(att => ({
        name: att.name,
        url: att.url,
        type: att.type,
        size: att.size
      })) : [],
      transcript: transcript || null
    };

    const response = await apiFetch({
      method: 'POST',
      endpoint: `conversations/${conversationId}/messages`,
      data: payload
    });
    return response.data;
  } catch (error) {
    console.error('Failed to send message:', error);
    throw new Error('Unable to send message. Please try again.');
  }
}

export async function uploadFile(file) {
  if (!file) {
    throw new Error('File is required');
  }
  if (file.size > 10 * 1024 * 1024) { // 10MB limit
    throw new Error('File size must be less than 10MB');
  }
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiFetch({
      method: 'POST',
      endpoint: 'chat/upload',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to upload file:', error);
    throw new Error('Unable to upload file. Please try again.');
  }
}



let messageHandler = null;

export function subscribeToMessages(conversationId, callback) {
  if (!socket) return;
  
  if (messageHandler) {
    socket.off('sub', messageHandler);
  }
  
  messageHandler = (data) => {
    if (data.coll === 'messages' && data.type === 'insert' && data.id === conversationId) {
      callback(data);
    }
  };
  
  socket.on('sub', messageHandler);
  socket.emit('sub', { coll: 'messages', type: 'insert', id: conversationId });
}

export function unsubscribeFromMessages(conversationId) {
  if (!socket) return;
  socket.emit('unsub', { coll: 'messages', type: 'insert', id: conversationId });
  if (messageHandler) {
    socket.off('sub', messageHandler);
    messageHandler = null;
  }
}

export async function markConversationRead(conversationId) {
  const response = await apiFetch({
    method: 'POST',
    endpoint: `conversations/${conversationId}/read`
  });
  return response.data;
}

export async function getUnreadCount() {
  try {
    const response = await apiFetch({
      method: 'GET',
      endpoint: 'conversations/unread/count'
    });
    return response.data?.count || 0;
  } catch (error) {
    console.error('Failed to get unread count:', error);
    return 0; // Fail silently for unread count
  }
}



export async function addParticipants(conversationId, userIds) {
  const response = await apiFetch({
    method: 'POST',
    endpoint: `conversations/${conversationId}/participants`,
    data: { userIds }
  });
  return response.data;
}

export async function leaveConversation(conversationId) {
  const response = await apiFetch({
    method: 'DELETE',
    endpoint: `conversations/${conversationId}/leave`
  });
  return response.data;
}

export async function renameConversation(conversationId, groupName) {
  const response = await apiFetch({
    method: 'PUT',
    endpoint: `conversations/${conversationId}/rename`,
    data: { groupName }
  });
  return response.data;
}

export async function deleteMessage(messageId) {
  const response = await apiFetch({
    method: 'DELETE',
    endpoint: `messages/${messageId}`
  });
  return response.data;
}

export async function editMessage(messageId, content) {
  const response = await apiFetch({
    method: 'PUT',
    endpoint: `messages/${messageId}`,
    data: { content }
  });
  return response.data;
}



export async function addReaction(messageId, emoji) {
  try {
    const response = await apiFetch({
      method: 'POST',
      endpoint: `messages/${messageId}/reactions`,
      data: { emoji }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to add reaction:', error);
    throw new Error('Unable to add reaction. Please try again.');
  }
}

export async function removeReaction(messageId, emoji) {
  try {
    const response = await apiFetch({
      method: 'DELETE',
      endpoint: `messages/${messageId}/reactions/${emoji}`
    });
    return response.data;
  } catch (error) {
    console.error('Failed to remove reaction:', error);
    throw new Error('Unable to remove reaction. Please try again.');
  }
}

let onlineStatusHandler = null;

export function subscribeToOnlineStatus(callback) {
  if (!socket) return;
  
  if (onlineStatusHandler) {
    socket.off('userOnline', onlineStatusHandler);
    socket.off('userOffline', onlineStatusHandler);
  }
  
  onlineStatusHandler = callback;
  
  socket.on('userOnline', (data) => callback({ type: 'online', userId: data.userId }));
  socket.on('userOffline', (data) => callback({ type: 'offline', userId: data.userId }));
}

export function unsubscribeFromOnlineStatus() {
  if (!socket || !onlineStatusHandler) return;
  
  socket.off('userOnline', onlineStatusHandler);
  socket.off('userOffline', onlineStatusHandler);
  onlineStatusHandler = null;
}
